import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react'
import clsx from 'clsx'
import { ReactNode, useCallback, useMemo, type JSX } from 'react'
import { create } from 'zustand'
import Button from '../Button/Button'
import Icon from '../Icon/Icon'
import { ImageStandAlone as Image } from '../Image/Image'

export interface ButtonsProps {
  text: string
  onCTAClick: () => void
}
export interface ModalProps {
  /**
   * If true, The close button will be hidden
   * Default value: true
   */
  persistent?: boolean
  /**
   * Information to help users
   * */
  notification?: string
  /**
   * Title of the modal
   */
  title?: string | ReactNode
  /**
   * Content of the modal
   */
  content: ReactNode | string
  /**
   * `id` of this modal. ModalButton will help this id to control specific modal
   */
  id: string
  /**
   * Accepts array of buttons for multiple button like NoteModal
   */
  buttons?: ButtonsProps[]
  /**
   * Left graphic image of modal
   */
  leftGraphicImage?: string
  /**
   * To append className of the Dialog Panel
   */
  dialogPanelClass?: string
  /**
   * To append className of the container
   */
  className?: string
  /**
   *  Optional before the modal starts showing
   */
  beforeShow?: () => void
  /**
   * Optional callback after the modal completely closes
   */
  afterHide?: () => void
  /**
   * Whether to show fullscreen (full viewport) modal
   */
  fullScreen?: boolean
  /**
   * Optional classNames for the children components
   */
  childrenClassNames?: Partial<Record<'Close' | 'Content', string>>
}

export interface ModalStore {
  openedDialogs: string[]
  dialogArgs: Record<string, any>
  openDialog: (id: string, args?: Record<string, any>) => void
  hideDialog: (id: string) => void
  getDialogArgs: (id: string) => any
}

export interface ModalButtonRenderProps {
  onClick: () => void
}

export interface ModalButtonProps {
  /**
   * `id` of the modal that will be controlled by this button
   */
  targetModal: string
  /**
   * A function that renders the intended button for the modal trigger
   */
  render: (buttonProps: ModalButtonRenderProps) => JSX.Element
  /**
   * A callback being called on onClick, to determine if modal should open
   */
  willModalOpen?: () => boolean | Promise<boolean>
  /**
   * optional modal arguments
   */
  args?: Record<string, any>
}

export const useModal = create<ModalStore>((set, get) => ({
  openedDialogs: [],
  dialogArgs: {},
  openDialog: (id, args = {}) => {
    const openedDialogs = get().openedDialogs
    if (!openedDialogs.includes(id)) {
      set({
        openedDialogs: [...openedDialogs, id],
        dialogArgs: { ...get().dialogArgs, [id]: args },
      })
    }
  },
  hideDialog: (id: string) => {
    const openedDialogs = get().openedDialogs
    const dialogArgs = { ...get().dialogArgs }
    delete dialogArgs[id]

    if (openedDialogs.includes(id)) {
      openedDialogs.splice(
        openedDialogs.findIndex((modalID) => id === modalID),
        1,
      )

      set({ openedDialogs: [...openedDialogs] })
    }
  },

  getDialogArgs: (id: string) => get().dialogArgs[id],
}))

export const ModalButton = ({
  render,
  targetModal,
  willModalOpen,
  args = {},
}: ModalButtonProps) => {
  const openDialog = useModal((state) => state.openDialog)

  const onClick = useCallback(async () => {
    const modalShouldOpen = willModalOpen ? await willModalOpen() : true

    if (modalShouldOpen) {
      openDialog(targetModal, args)
    }
  }, [args, openDialog, targetModal, willModalOpen])

  return render({ onClick })
}

export default function Modal({
  persistent,
  id,
  notification,
  title,
  content,
  buttons,
  leftGraphicImage,
  dialogPanelClass,
  className,
  beforeShow = () => {},
  afterHide = () => {},
  fullScreen = false,
  childrenClassNames,
}: ModalProps) {
  const openedDialogs = useModal((state) => state.openedDialogs)
  const hideDialog = useModal((state) => state.hideDialog)
  const isOpen = useMemo(() => openedDialogs.includes(id), [openedDialogs, id])

  return (
    <Transition
      show={isOpen}
      enter="ease-out duration-100"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200 delay-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      beforeEnter={beforeShow}
      afterLeave={afterHide}
    >
      <Dialog
        as="div"
        unmount={false}
        className="relative z-[300] focus:outline-none"
        onClose={() => !persistent && hideDialog(id)}
      >
        <div className="fixed inset-0 z-[300] w-screen overflow-y-auto">
          <div
            className={clsx(
              'fixed inset-0',
              !fullScreen && 'bg-theme-primary/50',
            )}
            aria-hidden="true"
          />
          <div
            className={clsx(
              'relative flex w-full items-center justify-center',
              fullScreen ? 'h-full' : 'min-h-full p-4',
            )}
            style={
              !fullScreen
                ? {
                    paddingTop: `calc(1rem + var(--braze-banner-height, 0px))`,
                  }
                : undefined
            }
          >
            <TransitionChild
              enter="ease-out duration-300 delay-100"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <DialogPanel
                className={clsx(
                  'flex bg-white',
                  fullScreen
                    ? 'w-full h-full overflow-auto'
                    : 'md:max-w-[54.75rem] rounded-lg drop-shadow-[0px_4px_4px_rgba(0,0,0,0.25)] overflow-visible',
                  dialogPanelClass,
                )}
              >
                {leftGraphicImage && (
                  <Image
                    src={leftGraphicImage}
                    alt="left graphic image"
                    className="left-graphic-image"
                  />
                )}
                <div
                  className={clsx(
                    'relative w-full md:max-w-[54.75rem] px-8 pt-14 pb-10 lg:p-12',
                    className,
                  )}
                >
                  {notification && (
                    <div className="bg-info-blue px-[1.875rem] py-4 rounded-lg type-body-7 mb-6 text-center flex items-center justify-center gap-2">
                      <Icon icon="error" className="w-6 h-6" />
                      <span>{notification}</span>
                    </div>
                  )}
                  {title ? (
                    <DialogTitle
                      as="h2"
                      className="type-body-1 mb-6 text-center"
                    >
                      {title}
                    </DialogTitle>
                  ) : null}
                  <div
                    className={clsx(
                      'type-body-8 [&_h3]:type-body-5 [&_h3]:mb-2 [&_p:not([class])]:mb-6',
                      childrenClassNames?.Content,
                    )}
                  >
                    {content}
                  </div>
                  {buttons && (
                    <div className="flex justify-center items-center gap-x-6 gap-y-2 flex-col md:flex-row">
                      {buttons.map(({ text, onCTAClick }, ctaKey) => (
                        <Button
                          onClick={onCTAClick}
                          className="md:max-w-[15.625rem] !min-h-12 md:!min-h-[55px] w-full type-body-7 lg:type-body-5"
                          key={ctaKey}
                        >
                          {text}
                        </Button>
                      ))}
                    </div>
                  )}
                  {!persistent && (
                    <button
                      className={clsx(
                        'w-6 h-6 flex items-center justify-center absolute right-6 top-6',
                        childrenClassNames?.Close,
                      )}
                      onClick={() => hideDialog(id)}
                    >
                      <Icon icon="close" className="w-full h-full" />
                    </button>
                  )}
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
